import { create } from 'zustand';
import { useSession } from '../../state/session.ts';
import { ApiError } from '../onboarding/api.ts';
import { findingsClient } from './client.ts';
import type { DiscoveryError, FindingsState, WorkspaceCredentials } from './types.ts';

const empty = {
  workspaceId: null,
  attemptId: null,
  attempt: null,
  findings: [],
  evidence: [],
  sourceRecords: [],
  findingEngagementLinks: [],
  loading: false,
  loaded: false,
  stale: false,
  error: null,
} satisfies Pick<
  FindingsState,
  | 'workspaceId'
  | 'attemptId'
  | 'attempt'
  | 'findings'
  | 'evidence'
  | 'sourceRecords'
  | 'findingEngagementLinks'
  | 'loading'
  | 'loaded'
  | 'stale'
  | 'error'
>;

const OFFLINE = "We can't reach Traceira right now. The last confirmed findings are shown.";

function toDiscoveryError(error: unknown): DiscoveryError {
  if (!(error instanceof ApiError)) return { message: OFFLINE };
  const code = /^[a-z0-9_.-]{1,80}$/i.test(error.code) ? error.code : undefined;
  return { message: error.message, ...(code ? { code } : {}) };
}

export const useWorkspaceFindings = create<FindingsState>()((set, get) => {
  let workspaceRevision = 0;
  let requestRevision = 0;

  return {
    ...empty,

    loadLatestAttemptFindings: async (
      workspaceId: string,
      credentials: WorkspaceCredentials = {},
      filters: {
        severity?: string;
        lane?: 'DNS' | 'Network' | 'Mixed';
        includeRetainedEvidence?: boolean;
      } = {},
    ) => {
      if (get().workspaceId !== workspaceId) {
        workspaceRevision += 1;
        requestRevision += 1;
        set({ ...empty, workspaceId });
      }
      const workspace = workspaceRevision;
      const request = ++requestRevision;
      const current = () => workspace === workspaceRevision && request === requestRevision;
      set({ loading: true, error: null });
      try {
        const result = await findingsClient.latestAttempt(workspaceId, credentials, filters);
        if (!current()) return;
        set({
          attemptId: result.attemptId,
          attempt: result.attempt,
          findings: result.findings,
          evidence: result.evidence,
          sourceRecords: result.sourceRecords,
          findingEngagementLinks: result.findingEngagementLinks,
          loading: false,
          loaded: true,
          stale: false,
          error: null,
        });
      } catch (error) {
        if (!current()) return;
        set((state) => ({
          loading: false,
          stale: state.loaded,
          error: toDiscoveryError(error),
        }));
      }
    },

    loadFinding: async (
      workspaceId: string,
      findingId: string,
      credentials: WorkspaceCredentials = {},
    ) => {
      findingId = findingId.trim();
      if (get().workspaceId !== workspaceId) {
        workspaceRevision += 1;
        requestRevision += 1;
        set({ ...empty, workspaceId });
      }
      const workspace = workspaceRevision;
      const request = ++requestRevision;
      const current = () => workspace === workspaceRevision && request === requestRevision;
      set({ loading: true, error: null });
      try {
        const [result, single] = await Promise.all([
          findingsClient.latestAttempt(workspaceId, credentials),
          findingsClient.fromLatestAttempt(workspaceId, findingId, credentials),
        ]);
        if (!current()) return;
        const findings = result.findings.slice();
        if (single) {
          const index = findings.findIndex((item) => item.findingId === findingId);
          if (index >= 0) findings[index] = { ...findings[index], ...single };
          else findings.push(single);
        }
        // A finding first seen in an earlier attempt cites evidence the latest attempt lacks:
        // fetch those records by id, reusing ones already fetched on an earlier refresh.
        const cited = findings.find((item) => item.findingId === findingId)?.evidenceIds ?? [];
        const inAttempt = new Set(result.evidence.map((item) => item.evidenceId));
        const fetched = new Map(get().evidence.map((item) => [item.evidenceId, item]));
        const older = await Promise.all(
          cited
            .filter((id) => !inAttempt.has(id))
            .map(
              (id) =>
                fetched.get(id) ??
                findingsClient.evidence(workspaceId, id, credentials).catch(() => null),
            ),
        );
        if (!current()) return;
        set({
          attemptId: result.attemptId,
          attempt: result.attempt,
          findings,
          evidence: [...result.evidence, ...older.filter((item) => item !== null)],
          sourceRecords: result.sourceRecords,
          findingEngagementLinks: result.findingEngagementLinks,
          loading: false,
          loaded: true,
          stale: false,
          error: null,
        });
      } catch (error) {
        if (!current()) return;
        set((state) => ({
          loading: false,
          stale: state.loaded,
          error: toDiscoveryError(error),
        }));
      }
    },

    reset: () => {
      workspaceRevision += 1;
      requestRevision += 1;
      set({ ...empty });
    },
  };
});

// Another workspace, or none after sign-out, never sees this one's records, even on pages that
// don't load them (search, the running badge).
useSession.subscribe((state, previous) => {
  if (state.workspace?.id !== previous.workspace?.id) useWorkspaceFindings.getState().reset();
});
